import { z } from 'zod';

/**
 * Zod schema for runtime configuration loaded from /runtime-config.json.
 * This file is generated by Terraform at deploy time with real values,
 * and ships with dev defaults in public/ for local development.
 */
export const RuntimeConfigSchema = z.object({
  cognitoUserPoolId: z.string(),
  cognitoClientId: z.string(),
  cognitoDomain: z.string(),
  apiBaseUrl: z.string(),
  version: z.string(),
});

export type RuntimeConfig = z.infer<typeof RuntimeConfigSchema>;

const DEV_DEFAULTS: RuntimeConfig = {
  cognitoUserPoolId: '',
  cognitoClientId: '',
  cognitoDomain: '',
  apiBaseUrl: '/api',
  version: 'dev',
};

let cachedConfig: RuntimeConfig | null = null;

function isDevMode(): boolean {
  return import.meta.env.DEV;
}

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (cachedConfig) {
    return cachedConfig;
  }

  if (isDevMode()) {
    cachedConfig = DEV_DEFAULTS;
    return cachedConfig;
  }

  const response = await fetch('/runtime-config.json');
  if (!response.ok) {
    throw new Error(
      `Failed to load runtime config: ${String(response.status)}`,
    );
  }

  const json: unknown = await response.json();
  cachedConfig = RuntimeConfigSchema.parse(json);
  return cachedConfig;
}

export function getRuntimeConfig(): RuntimeConfig {
  if (!cachedConfig) {
    throw new Error(
      'Runtime config not loaded. Call loadRuntimeConfig() first.',
    );
  }
  return cachedConfig;
}

/** Reset cached config — for testing only. */
export function resetRuntimeConfig(): void {
  cachedConfig = null;
}
